// helpers/tracking/eventMap.ts

/**
 * Mapping der Meta-Eventnamen auf GA4- und TikTok-Eventnamen.
 * Meta bleibt das führende Namensschema (siehe metaEvents.ts) –
 * hier wird nur pro Destination übersetzt.
 */

/** Meta-Standard-Events → von GA4 empfohlene Events */
const GA4_EVENT_MAP: Record<string, string> = {
    PageView: 'page_view',
    Search: 'search',
    Lead: 'generate_lead',
    ViewContent: 'view_item',
    AddToCart: 'add_to_cart',
    AddPaymentInfo: 'add_payment_info',
    InitiateCheckout: 'begin_checkout',
    Purchase: 'purchase',
    CompleteRegistration: 'sign_up',
};

/**
 * GA4-Eventname bestimmen.
 * Custom-Events (z.B. ContactCompleted) werden zu snake_case
 * (contact_completed) – GA4 erlaubt max. 40 Zeichen, [a-z0-9_].
 */
export function toGa4EventName(metaEvent: string): string {
    if (GA4_EVENT_MAP[metaEvent]) return GA4_EVENT_MAP[metaEvent];
    return metaEvent
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .replace(/[^a-zA-Z0-9_]/g, '_')
        .toLowerCase()
        .slice(0, 40);
}

/**
 * GA4-Parameter aufbereiten:
 * - nur Skalare (GA4 unterstützt keine Objekte/Arrays als Parameterwerte)
 * - Arrays/Objekte werden zu gekürztem JSON-String
 * - Search: search_string → search_term (GA4-Standardparameter)
 */
export function toGa4Params(
    metaEvent: string,
    params?: Record<string, unknown>,
): Record<string, unknown> | undefined {
    if (!params) return undefined;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(params)) {
        if (v === null || v === undefined) continue;
        if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
            out[k] = v;
        } else {
            try {
                out[k] = JSON.stringify(v).slice(0, 100);
            } catch { /* skip */ }
        }
    }
    if (metaEvent === 'Search' && typeof out.search_string === 'string') {
        out.search_term = out.search_string;
        delete out.search_string;
    }
    return out;
}

/**
 * Meta-Eventname → TikTok-Standard-Event (Bewerben-Journey).
 * PageView läuft über ttq.page(). Nicht gemappte Events gehen NICHT an TikTok.
 */
const TIKTOK_EVENT_MAP: Record<string, string> = {
    // Standortsuche: Institutionen im Umkreis gefunden
    InstitutionResponseReceived: 'FindLocation',
    // Institution angesehen/ausgewählt
    InstitutionResponseSelected: 'ViewContent',
    // Kontaktdaten vollständig
    ContactCompleted: 'Contact',
    // Zwischenschritte der Journey
    QuestionAnswered: 'ClickButton',
    AddressCompleted: 'ClickButton',
    AvailabilitiesSelected: 'ClickButton',
    // Bewerbung abgeschickt (TikToks Standard-Event für Bewerbungen)
    Lead: 'SubmitApplication',
};

export function toTikTokEventName(metaEvent: string): string | null {
    return TIKTOK_EVENT_MAP[metaEvent] || null;
}

/**
 * TikTok-Parameter im von TikTok empfohlenen Format:
 * contents[] mit content_id/content_type/content_name (+ value/currency,
 * bei der Bewerben-Journey gibt es keinen Warenwert → weggelassen).
 */
export function toTikTokParams(
    metaEvent: string,
    params?: Record<string, unknown>,
): Record<string, unknown> {
    const stepKey = typeof params?.step_key === 'string' ? params.step_key : undefined;
    const contentId = stepKey || metaEvent;
    const contentName =
        metaEvent === 'Lead'
            ? ((params?.lead_type as string) || 'Bewerbung')
            : `Bewerbung – ${stepKey || metaEvent}`;

    return {
        contents: [
            {
                content_id: contentId,
                content_type: 'product',
                content_name: contentName,
            },
        ],
    };
}
